import { getRouteApi } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '~/components/ui/common/Spinner';
import { DocumentOverviewSidebar } from '~/components/ui/document/dialogs/documentOverviewDialog/DocumentOverviewSidebar';
import { useDocumentOverviewApi } from '~/components/ui/document/dialogs/documentOverviewDialog/useDocumentOverviewApi';
import { useScrolledHighlight } from '~/components/ui/document/useScrollerHeight';
import { DocumentPDFViewer } from '~/components/ui/vendor/pdf/document_pdf_viewer/DocumentPdfViewer';
import { DocumentOverview } from '~/lib/pdfViewer/types';

const fundRouteApi = getRouteApi('/_authLayout/documents/fund/$fundId');
const portfolioRouteApi = getRouteApi(
  '/_authLayout/portfolio-company/$portfolioId',
);

type TabName = 'fields' | 'clauses';

export default function DocumentOverviewComponent({
  documentId,
  source = 'fundDocuments',
  overrideActiveHighlight,
}: DocumentOverview) {
  const { scrolledHighlight, scrollToHighlight } = useScrolledHighlight(source);
  const {
    pdfClauses,
    pdfFields,
    fieldTypes,
    loadingFields,
    loadingClauses,
    loadingDocumentUrl,
    addSearchMatchField,
    globalSearchPdfField,
  } = useDocumentOverviewApi({ documentId });
  const [activeTab, _rawSetTab] = useState<TabName>('fields');

  const searchParams =
    source === 'fundDocuments'
      ? fundRouteApi.useSearch()
      : portfolioRouteApi.useSearch();

  const setTab = useMemo(
    () => (tab: TabName) => {
      _rawSetTab(tab);
    },
    [],
  );
  useEffect(() => {
    if (searchParams.y2 || searchParams.x2) {
      addSearchMatchField({
        x1: searchParams.x1 as number,
        y1: searchParams.y1 as number,
        x2: searchParams.x2 as number,
        y2: searchParams.y2 as number,
        pageStart: searchParams.pageStart as number,
        pageEnd: searchParams.pageEnd as number,
      });
    }
  }, [searchParams]);
  const values = useMemo(() => {
    const result = [];
    if (activeTab === 'fields' && pdfFields) {
      for (const { fieldId, field, value } of pdfFields) {
        result.push({
          id: fieldId,
          name: field,
          value: value,
        });
      }
    }

    if (activeTab === 'clauses') {
      for (const clause of pdfClauses) {
        result.push({
          id: clause.clauseId,
          name: clause.title,
          value: clause.summary,
        });
      }
    }
    return result;
  }, [pdfFields, pdfClauses, activeTab]);

  return (
    <div className="container">
      {loadingFields || loadingClauses ? (
        <div className="flex align-items-center gap-2">
          <Spinner size={16} />
          <span>Loading fields and clauses</span>
        </div>
      ) : (
        <DocumentOverviewSidebar
          documentId={documentId}
          values={values}
          scrollToField={scrollToHighlight}
          showActions={activeTab === 'fields'}
          activeTab={activeTab}
          setTab={setTab}
        />
      )}

      <div className="pdfviewer">
        {loadingDocumentUrl ? (
          <div className="flex align-items-center gap-2">
            <Spinner size={16} />
            <span>Loading document</span>
          </div>
        ) : (
          <div style={{ height: 'calc(100% - 20px)', width: '100%' }}>
            <DocumentPDFViewer
              fieldTypes={fieldTypes}
              fields={activeTab === 'fields' ? pdfFields : undefined}
              clauses={activeTab === 'clauses' ? pdfClauses : undefined}
              documentId={documentId}
              activeHighlight={scrolledHighlight}
              overrideActiveHighlight={overrideActiveHighlight}
              onLoad={() => {
                if (globalSearchPdfField) {
                  setTimeout(
                    () => scrollToHighlight(globalSearchPdfField.fieldId),
                    1000,
                  );
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
